import { useCallback, useEffect, useState } from "react";
import { useAuthFetch } from "../utils/useAuthFetch";
import { IntegrationCatalogEntry } from "../types/integrationCatalog";

interface UseIntegrationCatalogResult {
  entries: IntegrationCatalogEntry[];
  loading: boolean;
  error: string | null;
  usingStub: boolean;
  refresh: () => Promise<void>;
}

const sharepointConfigSchema = {
  type: "object",
  required: ["tenant_id", "client_id"],
  properties: {
    tenant_id: {
      type: "string",
      title: "Tenant ID",
    },
    client_id: {
      type: "string",
      title: "Client ID",
    },
    client_secret: {
      type: "string",
      title: "Client secret",
      format: "password",
    },
  },
};

const sharepointWorkspaceSchema = {
  type: "object",
  "x-widget": "sharepoint_site_folder_picker",
  properties: {
    site_url: {
      type: "string",
      title: "SharePoint site",
    },
    selected_folders: {
      type: "array",
      "x-widget": "sharepoint_site_folder_picker",
      title: "Selected folders",
      items: { type: "string" },
    },
  },
};

const hubspotConfigSchema = {
  type: "object",
  required: ["token"],
  properties: {
    portal_id: {
      type: "string",
      title: "HubSpot hub ID",
    },
    base_url: {
      type: "string",
      title: "API base URL",
      default: "https://api.hubapi.com",
    },
    token: {
      type: "string",
      title: "Private app access token",
      format: "password",
    },
    client_secret: {
      type: "string",
      title: "OAuth client secret",
      format: "password",
    },
  },
};

const deltekConfigSchema = {
  type: "object",
  required: [
    "base_url",
    "client_id",
    "client_secret",
    "database",
    "username",
    "password",
  ],
  properties: {
    base_url: {
      type: "string",
      title: "Tenant base URL",
    },
    database: {
      type: "string",
      title: "Vantagepoint database name",
    },
    client_id: {
      type: "string",
      title: "Consumer Key / Client ID",
    },
    client_secret: {
      type: "string",
      title: "Consumer Secret",
      format: "password",
    },
    username: {
      type: "string",
      title: "Service account username",
    },
    password: {
      type: "string",
      title: "Service account password",
      format: "password",
    },
    api_prefix: {
      type: "string",
      title: "API path prefix",
      default: "/api",
    },
  },
};

export const mockIntegrationCatalogEntries: IntegrationCatalogEntry[] = [
  {
    key: "sharepoint",
    display_name: "SharePoint",
    description: "Connect Microsoft SharePoint document libraries.",
    category: "Documents",
    archetype: "org_workspace",
    auth_model: "oauth_client_credentials",
    status: "active",
    module_consumable: true,
    metadata_json: {
      config_schema: sharepointConfigSchema,
      workspace_schema: sharepointWorkspaceSchema,
      secret_fields: ["sp_cert_secret_reference"],
    },
    config_schema: sharepointConfigSchema,
    workspace_schema: sharepointWorkspaceSchema,
  },
  {
    key: "url_crawler",
    display_name: "URL crawler",
    description: "Crawl configured URLs into workspace knowledge.",
    category: "Documents",
    archetype: "org_workspace",
    auth_model: "none",
    status: "active",
    module_consumable: true,
    metadata_json: {
      config_schema: { type: "object", properties: {} },
      workspace_schema: {
        type: "object",
        required: ["urls"],
        properties: {
          urls: {
            type: "array",
            title: "URLs",
            items: { type: "string" },
          },
          max_depth: {
            type: "integer",
            title: "Max depth",
            default: 3,
          },
        },
      },
    },
  },
  {
    key: "hubspot",
    display_name: "HubSpot",
    description: "Connect HubSpot CRM for BKG enrichment and lookups.",
    category: "CRM",
    archetype: "org_only",
    auth_model: "private_app_token",
    status: "active",
    module_consumable: true,
    metadata_json: {
      config_schema: hubspotConfigSchema,
      workspace_schema: { type: "object", properties: {} },
      secret_fields: ["token", "client_secret"],
    },
    config_schema: hubspotConfigSchema,
  },
  {
    key: "deltek",
    display_name: "Deltek Vantagepoint",
    description:
      "Connect a dedicated Deltek Vantagepoint service account for organization skills.",
    category: "Professional services",
    archetype: "org_only",
    auth_model: "oauth_password",
    status: "active",
    module_consumable: true,
    metadata_json: {
      config_schema: deltekConfigSchema,
      workspace_schema: { type: "object", properties: {} },
      secret_fields: ["client_secret"],
    },
    config_schema: deltekConfigSchema,
  },
  {
    key: "email",
    display_name: "Email",
    description: "Connect organization email sending and mailbox settings.",
    category: "Communication",
    archetype: "org_workspace",
    auth_model: "secret_reference",
    status: "active",
    module_consumable: true,
    metadata_json: {
      config_schema: {
        type: "object",
        properties: {
          provider: {
            type: "string",
            title: "Provider",
            enum: ["smtp", "graph"],
          },
          api_key: {
            type: "string",
            title: "API key",
            format: "password",
          },
        },
      },
      workspace_schema: {
        type: "object",
        "x-widget": "email_senders_and_templates",
        properties: {
          sender_addresses: {
            type: "array",
            "x-widget": "email_senders_and_templates",
            items: { type: "string" },
          },
        },
      },
      secret_fields: ["api_key"],
    },
    workspace_schema: {
      type: "object",
      "x-widget": "email_senders_and_templates",
      properties: {
        sender_addresses: {
          type: "array",
          "x-widget": "email_senders_and_templates",
          items: { type: "string" },
        },
      },
    },
  },
  {
    key: "bkg",
    display_name: "BKG",
    description: "Select a BKG workspace as the source for IDA BKG skills.",
    category: "Knowledge",
    archetype: "org_workspace",
    auth_model: "none",
    status: "active",
    module_consumable: true,
    metadata_json: {
      config_schema: { type: "object", properties: {} },
      workspace_schema: {
        type: "object",
        properties: {},
      },
    },
    config_schema: { type: "object", properties: {} },
    workspace_schema: { type: "object", properties: {} },
  },
];

const normalizeCatalogResponse = (response: any): IntegrationCatalogEntry[] => {
  const rawEntries = Array.isArray(response)
    ? response
    : response?.items || response?.entries || response?.catalog || [];

  if (!Array.isArray(rawEntries)) return [];

  return rawEntries.map((entry) => ({
    ...entry,
    config_schema:
      entry.config_schema || entry.metadata_json?.config_schema || null,
    workspace_schema:
      entry.workspace_schema || entry.metadata_json?.workspace_schema || null,
    org_integration_id:
      entry.org_integration_id || entry.organization_integration_id || null,
  }));
};

export const useIntegrationCatalog = (
  organizationId?: string | null,
  workspaceId?: string | null,
  includeDeprecated = false,
): UseIntegrationCatalogResult => {
  const authFetch = useAuthFetch();
  const [entries, setEntries] = useState<IntegrationCatalogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingStub, setUsingStub] = useState(false);

  const refresh = useCallback(async () => {
    if (!organizationId && !workspaceId) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (workspaceId) {
        params.set("workspace_id", workspaceId);
      }
      if (includeDeprecated) {
        params.set("include_deprecated", "true");
      }
      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await authFetch(
        `/api/integration_catalog${query}`,
        "fetch integration catalog",
      );
      const normalized = normalizeCatalogResponse(response);
      setEntries(normalized);
      setUsingStub(false);
    } catch (err: any) {
      console.warn("Integration catalog endpoint unavailable; using stub", err);
      setEntries(mockIntegrationCatalogEntries);
      setUsingStub(true);
      setError(
        "Using local catalog metadata until the backend catalog endpoint is available.",
      );
    } finally {
      setLoading(false);
    }
  }, [authFetch, includeDeprecated, organizationId, workspaceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { entries, loading, error, usingStub, refresh };
};

export const catalogKeyForLegacyType = (type?: string | null) => {
  switch (type) {
    case "sp_site":
      return "sharepoint";
    case "url_crawler":
      return "url_crawler";
    case "email":
      return "email";
    case "bkg":
    case "bkg_data_access":
      return "bkg";
    default:
      return type || "";
  }
};
